import { useEffect, useRef, useState } from "react";
import { Loader2, Play, Pause, Square, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE } from "@/lib/api";

/**
 * Inline-kontroller för Edit-uppläsning av ett material.
 *
 * - **Chunked playback**: Materialet delas i ~2500-tecken-chunks som spelas i kö.
 *   När nuvarande chunk närmar sig slutet (75 % uppspelat) pre-fetchas nästa.
 *   Stoppar användaren tidigt → resterande chunks genereras aldrig (sparar TTS).
 * - **MediaSession API**: titel/play/pause/stop syns på låsskärm, AirPods,
 *   bilstereo etc.
 * - **<audio>-element med token-via-query** så browsern hanterar play/pause
 *   utan att förlora user-gesture (annars blockerar Safari/iOS).
 */
export function MaterialAudioControls({ materialId, filename, compact = false }) {
  const [state, setState] = useState("idle"); // idle | loading | playing | paused
  const [chunkInfo, setChunkInfo] = useState(null); // { total_chunks, est_duration_sec }
  const [currentChunk, setCurrentChunk] = useState(0);
  const [resumeChunk, setResumeChunk] = useState(0); // var användaren slutade
  const audioRef = useRef(null);
  const nextAudioRef = useRef(null); // pre-fetchad nästa chunk
  const prefetchedIdxRef = useRef(-1);

  const base = API_BASE || "";
  const token =
    (typeof window !== "undefined" && localStorage.getItem("grindstugatan_token")) || "";

  const buildChunkSrc = (idx) =>
    `${base}/api/materials/${materialId}/speak?voice=onyx&chunk=${idx}&token=${encodeURIComponent(token)}`;

  // Vid mount: hämta tidigare progress så vi kan visa "Fortsätt från del N"
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/materials/${materialId}/speak/progress`);
        if (cancelled) return;
        if (r.data && typeof r.data.chunk_index === "number" && r.data.chunk_index > 0) {
          setResumeChunk(r.data.chunk_index);
          // Sätt även chunkInfo så badgen visas direkt vid mount
          if (r.data.total_chunks) {
            setChunkInfo((prev) => prev || { total_chunks: r.data.total_chunks });
          }
        }
      } catch (_e) {
        /* progress saknas — ok */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [materialId]);

  // Spara progress vid chunk-byte / stopp / pause
  const persistProgress = (chunkIndex, totalChunks) => {
    // Använd fetch i bakgrunden — vi bryr oss inte om svar
    api
      .post(`/materials/${materialId}/speak/progress`, {
        chunk_index: chunkIndex,
        total_chunks: totalChunks,
      })
      .catch(() => {});
  };

  const ensureMediaSession = (idx, total) => {
    if (typeof navigator === "undefined" || !navigator.mediaSession) return;
    const part = total > 1 ? ` (${idx + 1}/${total})` : "";
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: (filename || "Material") + part,
      artist: "Edit läser upp",
      album: "Familjen Grindstugatan",
    });
    navigator.mediaSession.setActionHandler("play", () => handlePlay());
    navigator.mediaSession.setActionHandler("pause", () => handlePause());
    navigator.mediaSession.setActionHandler("stop", () => handleStop());
  };

  const attachAudioEvents = (audio, idx, total) => {
    audio.preload = "auto";
    audio.onloadstart = () => setState("loading");
    audio.oncanplay = () => {
      if (audio.paused) setState((s) => (s === "loading" ? "paused" : s));
    };
    audio.onplaying = () => setState("playing");
    audio.onpause = () => {
      if (!audio.ended) setState((s) => (s === "playing" ? "paused" : s));
    };
    audio.ontimeupdate = () => {
      // Pre-fetch nästa chunk när 75 % uppspelat
      const d = audio.duration;
      if (!d || !isFinite(d)) return;
      const ratio = audio.currentTime / d;
      const nextIdx = idx + 1;
      if (
        ratio >= 0.75 &&
        nextIdx < total &&
        prefetchedIdxRef.current !== nextIdx &&
        !nextAudioRef.current
      ) {
        prefetchedIdxRef.current = nextIdx;
        const next = new Audio(buildChunkSrc(nextIdx));
        next.preload = "auto";
        // Trigga nedladdning genom .load() (vissa browsers laddar bara annars vid play)
        try {
          next.load();
        } catch (_e) {
          // ignore
        }
        nextAudioRef.current = next;
      }
    };
    audio.onended = () => {
      // Är det fler chunks?
      const nextIdx = idx + 1;
      // Spara progress (eller rensa om vi är klara — backend hanterar det)
      persistProgress(nextIdx, total);
      if (nextIdx < total) {
        const next = nextAudioRef.current || new Audio(buildChunkSrc(nextIdx));
        nextAudioRef.current = null;
        prefetchedIdxRef.current = -1;
        audioRef.current = next;
        attachAudioEvents(next, nextIdx, total);
        setCurrentChunk(nextIdx);
        ensureMediaSession(nextIdx, total);
        next.play().catch((e) => {
          toast.error("Fortsatte inte: " + (e?.message || e));
          setState("idle");
        });
      } else {
        setState("idle");
        setCurrentChunk(0);
        setResumeChunk(0);
        if (navigator.mediaSession) navigator.mediaSession.playbackState = "none";
        audioRef.current = null;
      }
    };
    audio.onerror = () => {
      toast.error("Kunde inte spela upp uppläsningen");
      setState("idle");
      audioRef.current = null;
      nextAudioRef.current = null;
    };
  };

  const handlePlay = async () => {
    // Om paused → bara fortsätt
    if (audioRef.current && state === "paused") {
      try {
        await audioRef.current.play();
        if (navigator.mediaSession) navigator.mediaSession.playbackState = "playing";
      } catch (e) {
        toast.error("Kunde inte fortsätta: " + (e?.message || e));
      }
      return;
    }
    // Ny uppspelning — hämta chunk-info först
    let info = chunkInfo;
    if (!info) {
      try {
        setState("loading");
        const r = await api.get(`/materials/${materialId}/speak/info`);
        info = r.data;
        setChunkInfo(info);
      } catch (e) {
        toast.error("Kunde inte starta uppläsning: " + (e?.response?.data?.detail || e.message));
        setState("idle");
        return;
      }
    }
    const total = info.total_chunks || 1;
    // Börja från sparad position om resume-chunk är inom giltigt intervall
    const idx = resumeChunk > 0 && resumeChunk < total ? resumeChunk : 0;
    if (idx > 0) {
      toast.message(`Fortsätter från del ${idx + 1} av ${total}`);
    }
    const audio = new Audio(buildChunkSrc(idx));
    attachAudioEvents(audio, idx, total);
    audioRef.current = audio;
    setCurrentChunk(idx);
    ensureMediaSession(idx, total);
    try {
      await audio.play();
      if (navigator.mediaSession) navigator.mediaSession.playbackState = "playing";
    } catch (e) {
      const reason =
        e?.name === "NotAllowedError"
          ? "Klicka igen för att starta uppspelning (browser kräver direktklick)"
          : `Uppspelning misslyckades: ${e?.message || e}`;
      toast.error(reason);
      setState("idle");
    }
  };

  const handlePause = () => {
    const audio = audioRef.current;
    if (audio && !audio.paused) {
      audio.pause();
      // Spara position så användaren kan fortsätta från samma chunk senare
      const total = chunkInfo?.total_chunks || 1;
      persistProgress(currentChunk, total);
      if (navigator.mediaSession) navigator.mediaSession.playbackState = "paused";
    }
  };

  const handleStop = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      audioRef.current = null;
    }
    if (nextAudioRef.current) {
      nextAudioRef.current.pause();
      nextAudioRef.current = null;
    }
    // Stop = manuell paus, behåll position så att play återupptar därifrån
    const total = chunkInfo?.total_chunks || 1;
    if (currentChunk > 0) {
      persistProgress(currentChunk, total);
      setResumeChunk(currentChunk);
    }
    prefetchedIdxRef.current = -1;
    setState("idle");
    if (navigator.mediaSession) navigator.mediaSession.playbackState = "none";
  };

  // Börja om från noll (rensa sparad progress)
  const handleRestart = async () => {
    try {
      await api.delete(`/materials/${materialId}/speak/progress`);
    } catch (_e) {
      /* ok */
    }
    setResumeChunk(0);
    toast.success("Börjar om från början");
  };

  // Cleanup när komponenten unmountas
  useEffect(() => {
    return () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if (nextAudioRef.current) {
        nextAudioRef.current.pause();
        nextAudioRef.current = null;
      }
    };
  }, []);

  const isPlaying = state === "playing";
  const isLoading = state === "loading";
  const isActive = isPlaying || state === "paused";
  const total = chunkInfo?.total_chunks || 1;
  const showResume = !isActive && resumeChunk > 0 && total > 1;

  const sizeBtn = compact ? "w-7 h-7" : "w-8 h-8";
  const sizeIcon = compact ? 12 : 14;

  return (
    <div
      className="inline-flex items-center gap-1.5 flex-wrap"
      data-testid={`material-audio-${materialId}`}
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        onClick={isPlaying ? handlePause : handlePlay}
        disabled={isLoading}
        className={`${sizeBtn} rounded-full bg-[color:var(--jarvis)] text-white flex items-center justify-center disabled:opacity-50 hover:opacity-90 active:scale-95 transition`}
        title={
          isPlaying
            ? "Pausa"
            : isLoading
            ? "Laddar…"
            : showResume
            ? `Fortsätt från del ${resumeChunk + 1}/${total}`
            : "Lyssna (Edit läser upp)"
        }
        aria-label={isPlaying ? "Pausa uppläsning" : "Spela upp"}
        data-testid={`material-audio-play-${materialId}`}
      >
        {isLoading ? (
          <Loader2 size={sizeIcon} className="animate-spin" />
        ) : isPlaying ? (
          <Pause size={sizeIcon} />
        ) : (
          <Play size={sizeIcon} className="ml-0.5" />
        )}
      </button>
      {/* Resume-info när inte aktiv */}
      {showResume && (
        <>
          <span
            className="text-[10px] text-[color:var(--text-secondary)] tabular-nums"
            data-testid={`material-audio-resume-${materialId}`}
          >
            ▶ Fortsätt {resumeChunk + 1}/{total}
          </span>
          <button
            type="button"
            onClick={handleRestart}
            className={`${sizeBtn} rounded-full bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] flex items-center justify-center hover:bg-[color:var(--bg-soft)] active:scale-95 transition`}
            title="Börja om från början"
            aria-label="Börja om från början"
            data-testid={`material-audio-restart-${materialId}`}
          >
            <RotateCcw size={sizeIcon - 2} />
          </button>
        </>
      )}
      {isActive && (
        <>
          <button
            type="button"
            onClick={handleStop}
            className={`${sizeBtn} rounded-full bg-[color:var(--bg-elevated)] text-[color:var(--text-secondary)] flex items-center justify-center hover:bg-[color:var(--bg-soft)] active:scale-95 transition`}
            title="Stoppa"
            aria-label="Stoppa uppläsning"
            data-testid={`material-audio-stop-${materialId}`}
          >
            <Square size={sizeIcon - 2} />
          </button>
          {total > 1 && (
            <span
              className="text-[10px] text-[color:var(--text-tertiary)] tabular-nums"
              data-testid={`material-audio-progress-${materialId}`}
            >
              Del {currentChunk + 1}/{total}
            </span>
          )}
        </>
      )}
    </div>
  );
}
